import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth-context";
import { getMyDigest, type DigestData, type DigestScope } from "@/lib/digest.functions";
import { CATEGORIES, type Category } from "@/lib/categories";
import { SEVERITY_META, STATUS_META, type Severity } from "@/lib/reports";
import { Loader2, MapPin, CheckCircle2, Flame, TrendingUp, Newspaper, Building2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/trends")({
  component: TrendsPage,
  head: () => ({
    meta: [
      { title: "Xamseen — Trends" },
      { name: "description", content: "Weekly civic trends for your neighborhood, city, or country." },
    ],
  }),
});

function TrendsPage() {
  const { user, profile } = useAuth();
  const [scope, setScope] = useState<DigestScope>("city");
  const [digest, setDigest] = useState<DigestData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    getMyDigest().then((d) => { setDigest(d); setLoading(false); });
  }, [user, scope]);

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Newspaper className="h-4 w-4 text-primary" /> Your civic digest
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Trends</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A weekly snapshot of reports, resolutions, and official activity near you.
          </p>
        </header>

        {!user ? (
          <Card className="p-8 text-center">
            <p className="font-medium">Sign in to see your local trends</p>
            <p className="mt-1 text-sm text-muted-foreground">We build a personalized digest from your profile location.</p>
            <Button asChild className="mt-4"><Link to="/auth">Sign in</Link></Button>
          </Card>
        ) : (
          <>
            <Tabs value={scope} onValueChange={(v) => setScope(v as DigestScope)}>
              <TabsList>
                <TabsTrigger value="neighborhood">Neighborhood</TabsTrigger>
                <TabsTrigger value="city">City</TabsTrigger>
                <TabsTrigger value="country">Country</TabsTrigger>
              </TabsList>
            </Tabs>

            {loading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : digest ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Kpi icon={TrendingUp} label="New reports" value={digest.newReports} />
                  <Kpi icon={CheckCircle2} label="Resolved" value={digest.resolvedReports} color="text-success" />
                  <Kpi icon={Flame} label="Hotspots" value={digest.activeHotspots} color="text-warning" />
                  <Kpi icon={MapPin} label="Scope" value={digest.locationLabel} />
                </div>

                <Card className="p-5">
                  <h2 className="mb-4 font-display text-base font-semibold">Top categories this week</h2>
                  {digest.topCategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No category data yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {digest.topCategories.map((row) => {
                        const meta = CATEGORIES[row.key as Category];
                        const max = Math.max(1, ...digest.topCategories.map((r) => r.count));
                        return (
                          <div key={row.key}>
                            <div className="mb-1 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-1.5">
                                {meta ? <meta.icon className="h-3.5 w-3.5" style={{ color: meta.color }} /> : null}
                                {meta ? meta.label : row.key}
                              </span>
                              <span className="text-muted-foreground">{row.count}</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-muted">
                              <div className="h-full rounded-full" style={{ width: `${(row.count / max) * 100}%`, background: meta?.color ?? "#64748B" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>

                <Card className="p-5">
                  <h2 className="mb-4 font-display text-base font-semibold">Recent reports</h2>
                  {digest.recentReports.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No new reports this week.</p>
                  ) : (
                    <ul className="space-y-2">
                      {digest.recentReports.map((r: any) => {
                        const cat = CATEGORIES[r.category as Category];
                        const sev = SEVERITY_META[(r.severity ?? "info") as Severity];
                        return (
                          <li key={r.id}>
                            <Link to="/posts/$id" params={{ id: r.id }}
                              className="flex items-start gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40">
                              <span className="mt-0.5 shrink-0 rounded-full p-1.5" style={{ background: cat.color + "15" }}>
                                <cat.icon className="h-3.5 w-3.5" style={{ color: cat.color }} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">{r.title}</span>
                                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                  <Badge variant="outline" className={cn("font-normal", sev.badge)}>{sev.label}</Badge>
                                  <span>{cat.label}</span>
                                  <span>· {STATUS_META[r.status]?.label ?? r.status}</span>
                                  <span>· {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                                  {r.location_name && <span>· {r.location_name}</span>}
                                </span>
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Card>

                <Card className="p-5">
                  <h2 className="mb-4 font-display text-base font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Official responses</h2>
                  {digest.officialResponses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No official responses this week.</p>
                  ) : (
                    <ul className="space-y-3">
                      {digest.officialResponses.map((r: any) => (
                        <li key={r.id} className="rounded-xl border border-border bg-accent/30 p-4">
                          <p className="text-sm font-medium">{r.post?.title ?? "Report"}</p>
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{r.body}</p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                            {r.post?.location_name && <> · {r.post.location_name}</>}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">Could not load trends.</p>
              </Card>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function Kpi({ icon: Icon, label, value, color }: { icon: typeof TrendingUp; label: string; value: string | number; color?: string }) {
  return (
    <Card className="p-4">
      <Icon className={cn("mb-2 h-5 w-5", color ?? "text-primary")} />
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </Card>
  );
}
